import React, { useMemo, useState } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { Radio, RadioChangeEvent, Space } from 'antd';
import dayjs from 'dayjs';

import { SalaryObject } from 'components/_shared/SalaryField/types';
import { Redux } from 'helpers/types/_common';
import { BCDay, BCJointStatus, BCMonth, BCStatuses } from 'api/TimeSheet/types';
import { FULL_MONTH_YEAR_FORMAT } from 'helpers/constants/_common/timeFormats';

import s from './styles.module.sass';
import { FlatUserHours } from '../../../../../Table/components/TableBody/helpers/types';
import { selectedActivitiesByHours } from './helpers/helpers';
import MonthInfoPopoverItem from '../MonthInfoPopoverItem/MonthInfoPopoverItem';
import MonthInfoReplaceModal from '../MonthInfoReplaceModal/MonthInfoReplaceModal';
import { MonthInfoModalState } from '../../helpers/types';
import MonthInfoTotalRow from '../MonthInfoTotalRow/MonthInfoTotalRow';
import { REGULAR_WORK_DAY_HOURS, MONTH_TYPES, MonthValue } from '../../constants';

interface MonthInfoPopoverProps {
  BCMonth: BCMonth;
  flatUserHoursTable: FlatUserHours;
  holidaysHours: number;
  userId: number;
  visible: boolean;
  curatorOnlyView?: boolean;
}

const MonthInfoPopover: React.FC<MonthInfoPopoverProps> = ({ flatUserHoursTable, userId, holidaysHours, BCMonth, curatorOnlyView }) => {
  const { isMonthClosed } = useSelector((state: Redux) => state.timeSheet.tableSettings);

  const [ modalInfo, setModalInfo ] = useState<MonthInfoModalState>({
    visible: false,
    partner: null,
  });

  const [ salaryObject, setSalaryObject ] = useState<SalaryObject>({
    hourly: 0,
    fixed: 0,
  });

  const [value, setValue] = useState<MonthValue>(MONTH_TYPES.BUSINESS);

  const selectedActivities = useMemo(() => selectedActivitiesByHours(flatUserHoursTable, BCMonth, value), [ flatUserHoursTable, value ]);
  const handleSetMonthType = (e: RadioChangeEvent) => {
    setValue(e.target.value);
  };
  const renderMonthStats = () => {
    let workDaysCount: number = 0;
    let workHoursCount: number = 0;
    let workDays: Array<BCDay> = [];
    if (value == MONTH_TYPES.BUSINESS) {
      workDays = _.filter(
        BCMonth.jointDays,
        (item) => {
          return item.status == BCStatuses.Regular && (item.jointStatus == BCJointStatus.Basic || item.jointStatus == BCJointStatus.Additional)
        } 
      );
    } else {
      workDays = _.filter(
        BCMonth.jointDays,
        (item) => {
          return item.status == BCStatuses.Regular && (item.jointStatus == BCJointStatus.Basic || item.jointStatus == BCJointStatus.Cut)
        } 
      );
    }
    workDaysCount = workDays.length;
    workHoursCount = workDaysCount * REGULAR_WORK_DAY_HOURS;
    const monthName = dayjs(`${BCMonth.year}-${BCMonth.month}-1`).format(FULL_MONTH_YEAR_FORMAT);

    return <div className={s.monthStatsWrapper}>
       <Space direction="horizontal">
        <div className={s.monthName}>{`${monthName}: `}</div>
        <div className={s.monthStats}>{`${workDaysCount} workdays/${workHoursCount} hours`}</div>
       </Space>
      </div>
  };

  const renderTotal = () => {
    if (_.isEmpty(selectedActivities)) {
      return null;
    }

    return (
      <MonthInfoTotalRow
        selectedActivities={selectedActivities}
        holidaysHours={holidaysHours}
        salaryObject={salaryObject}
        setSalaryObject={setSalaryObject}
      />
    );
  };

  return (
    <div className={s.monthPopoverContent}>
      <div className={s.monthTypeRadioWrapper}>
        <Radio.Group
        defaultValue={MONTH_TYPES.BUSINESS}
        onChange={handleSetMonthType}
        className={s.monthTypeRadio}
      >
        <Radio.Button
          value={MONTH_TYPES.BUSINESS}
        >
          {MONTH_TYPES.BUSINESS}
        </Radio.Button>
        <Radio.Button
          value={MONTH_TYPES.REGULAR}
        >
          {MONTH_TYPES.REGULAR}
        </Radio.Button>
      </Radio.Group>
      </div>

      { renderMonthStats() }
      
      <div className={s.monthPopoverList}>
        {
          _.isEmpty(selectedActivities) && (
            <div className={s.nothingWrapper}>
              <span className={s.nothingText}>No tracked activities yet</span>
            </div>
          )
        }
        {
          _.map(selectedActivities, partner => (
            <MonthInfoPopoverItem
              partner={partner}
              salaryObject={salaryObject}
              key={partner.activity.activityId}
              setModalInfo={setModalInfo}
              isMonthClosed={isMonthClosed}
              curatorOnlyView={curatorOnlyView}
              monthType={value}
            />
          ))
        }
      </div>

      { renderTotal() }

      {
        !isMonthClosed && (
          <MonthInfoReplaceModal
            userId={userId}
            modalInfo={modalInfo}
            setModalInfo={setModalInfo}
            selectedActivities={selectedActivities}
            BCMonth={BCMonth}
          />
        )
      }

    </div>
  );
};

export default MonthInfoPopover;
